/**
 * Tamper detection policy (ADR-0012, docs/sandbox.md §6): which APIs the
 * prelude guards so overwriting them is recorded live, which objects the
 * end-of-run global diff compares, and what a change to each path means for
 * analysis (core/analysis/rules/tamper.ts).
 *
 * Paths are where the guest finds a property: `window.<name>` for globals,
 * otherwise `<Global>.<…>.<name>` (e.g. `XMLHttpRequest.prototype.open`).
 */

/** What tampering with an API lets a script do. */
export type TamperGroup =
  /** Intercept or rewrite the page's own traffic (MITM). */
  | "network"
  /** Observe or suppress the page's events and DOM operations. */
  | "events"
  /** Intercept dynamic code. */
  | "codegen"
  /** Blind or fool an analysis tool (toString, descriptors). */
  | "instrumentation"
  /** Change built-in behavior for every object (prototype pollution). */
  | "intrinsics"
  /** Intercept the user's crypto wallet (the `ethereum` decoy's methods). */
  | "wallet";

/**
 * Data properties the prelude turns into accessor guards (presented as data
 * properties, docs/sandbox.md §6), so an assignment is recorded as
 * `globals`/`set` tagged `tamper`. Prototypes of the simulated DOM
 * (linkedom) are watched by its membrane instead: any overwrite of an
 * existing member is recorded the same way.
 */
export const GUARDED_APIS: readonly string[] = [
  "window.fetch",
  "window.XMLHttpRequest",
  "window.WebSocket",
  "window.EventSource",
  "window.Request",
  "window.Response",
  "window.Headers",
  "window.Image",
  "window.addEventListener",
  "window.removeEventListener",
  "window.dispatchEvent",
  "window.postMessage",
  "window.open",
  "window.setTimeout",
  "window.setInterval",
  "window.Function",
  "window.atob",
  "window.btoa",
  "window.WebAssembly",
  "XMLHttpRequest.prototype.open",
  "XMLHttpRequest.prototype.send",
  "XMLHttpRequest.prototype.setRequestHeader",
  "WebSocket.prototype.send",
  "Navigator.prototype.sendBeacon",
  "Storage.prototype.getItem",
  "Storage.prototype.setItem",
  "Function.prototype.toString",
  "Function.prototype.call",
  "Function.prototype.apply",
  "Function.prototype.bind",
  "Object.getOwnPropertyDescriptor",
  "Object.getOwnPropertyDescriptors",
  "Object.getOwnPropertyNames",
  "Object.defineProperty",
  "Object.defineProperties",
  "Reflect.getOwnPropertyDescriptor",
  "Reflect.defineProperty",
  "Reflect.ownKeys",
  "JSON.stringify",
  "JSON.parse",
];

/**
 * Globals whose mere reading is a classic sandbox or headless-browser check
 * (`!window.chrome`, `typeof WebAssembly`). They are guarded too, and a
 * read records `fingerprint`/`get` tagged `sandbox-check`.
 */
export const SANDBOX_CHECK_READS: readonly string[] = ["window.chrome", "window.WebAssembly"];

/**
 * Objects whose own properties the end-of-run diff compares with boot time
 * (besides the global object itself, reported as plain names), and whose
 * `Object.defineProperty` changes are recorded live. Intrinsic prototypes
 * are where prototype pollution shows up.
 */
export const DIFF_ROOTS: readonly string[] = [
  "Object.prototype",
  "Array.prototype",
  "Function.prototype",
  "String.prototype",
  "Number.prototype",
  "Promise.prototype",
  "RegExp.prototype",
  "Object",
  "Reflect",
  "JSON",
  "XMLHttpRequest.prototype",
  "WebSocket.prototype",
  "Navigator.prototype",
  "Storage.prototype",
  "EventTarget.prototype",
  "Node.prototype",
  "Element.prototype",
  "HTMLElement.prototype",
  "Document.prototype",
  "HTMLFormElement.prototype",
  "HTMLInputElement.prototype",
];

/** Built-in prototypes every object inherits from: new members there pollute everything. */
const INTRINSIC_PROTOTYPES = [
  "Object.prototype",
  "Array.prototype",
  "Function.prototype",
  "String.prototype",
  "Number.prototype",
  "Promise.prototype",
  "RegExp.prototype",
];

const INSTRUMENTATION = new Set([
  "Function.prototype.toString",
  "Function.prototype.call",
  "Function.prototype.apply",
  "Function.prototype.bind",
  "Object.getOwnPropertyDescriptor",
  "Object.getOwnPropertyDescriptors",
  "Object.getOwnPropertyNames",
  "Object.defineProperty",
  "Object.defineProperties",
  "Reflect.getOwnPropertyDescriptor",
  "Reflect.defineProperty",
  "Reflect.ownKeys",
  "JSON.stringify",
  "JSON.parse",
  "Error.prepareStackTrace",
]);

const NETWORK_GLOBALS = new Set([
  "window.fetch",
  "window.XMLHttpRequest",
  "window.WebSocket",
  "window.EventSource",
  "window.Request",
  "window.Response",
  "window.Headers",
  "window.Image",
  "window.open",
  "Navigator.prototype.sendBeacon",
  "HTMLFormElement.prototype.submit",
]);

const NETWORK_PROTOTYPES = ["XMLHttpRequest.prototype.", "WebSocket.prototype."];

const EVENT_GLOBALS = new Set([
  "window.addEventListener",
  "window.removeEventListener",
  "window.dispatchEvent",
  "window.postMessage",
]);

const EVENT_PROTOTYPES = [
  "EventTarget.prototype.",
  "Node.prototype.",
  "Element.prototype.",
  "HTMLElement.prototype.",
  "Document.prototype.",
  "HTMLFormElement.prototype.",
  "HTMLInputElement.prototype.",
];

const CODEGEN = new Set([
  "window.eval",
  "window.Function",
  "window.setTimeout",
  "window.setInterval",
  "window.WebAssembly",
]);

const GLOBAL = "window.";

/**
 * The event-style path of a global-diff path: globals are diffed by bare
 * name (`fetch`), everything else by its full path.
 */
export const tamperPath = (path: string): string =>
  path.includes(".") ? path : `${GLOBAL}${path}`;

/** What tampering with `path` (an event target or a `tamperPath`) means, or null if nothing notable. */
export function tamperGroup(path: string): TamperGroup | null {
  if (INSTRUMENTATION.has(path)) return "instrumentation";
  if (NETWORK_GLOBALS.has(path) || NETWORK_PROTOTYPES.some((p) => path.startsWith(p))) {
    return "network";
  }
  if (EVENT_GLOBALS.has(path) || EVENT_PROTOTYPES.some((p) => path.startsWith(p))) {
    return "events";
  }
  if (CODEGEN.has(path)) return "codegen";
  if (path.startsWith("window.ethereum.")) return "wallet";
  if (INTRINSIC_PROTOTYPES.some((p) => path.startsWith(`${p}.`))) return "intrinsics";
  return null;
}
